const getCookie = (cookieName) => {
    if (!cookieName) {
        return null;
    }

    const name          = `${cookieName}=`;
    const decodedCookie = document.cookie ? document.cookie : '';
    const cookieArray   = decodedCookie.split(';');

    for (let i = 0; i < cookieArray.length; i += 1) {
        const cookie = cookieArray[i].trim();

        if (!cookie.startsWith(name)) {
            // eslint-disable-next-line no-continue
            continue;
        }

        const value = cookie.substring(name.length);

        try {
            return decodeURIComponent(value);
        } catch (error) {
            return null;
        }
    }

    return null;
};

const setCookie = (cookieName, cookieValue, {
    days = 365,
    path = '/'
} = {}) => {
    if (!cookieName) {
        throw new Error('Cookie name is required.');
    }

    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    const encodedValue = encodeURIComponent(cookieValue);
    const cookieParts  = [
        `${cookieName}=${encodedValue}`,
        `expires=${expires.toUTCString()}`,
        `path=${path}`
    ];

    document.cookie = cookieParts.join(';');
};

/**
 * Cookies panel controller.
 */
class CookiesPanel {
    /**
     * @param {Object} options - Panel options.
     * @param {string} options.cookieName - Cookie name to track acceptance.
     * @param {HTMLElement} options.wrapper - Panel wrapper element.
     * @param {HTMLElement} options.button - Accept/close button element.
     * @param {string} [options.showClass='is-show'] - CSS class to show panel.
     * @param {number} [options.expiresDays=365] - Cookie lifetime in days.
     * @param {string} [options.path='/'] - Cookie path.
     */
    constructor({
        cookieName,
        wrapper,
        button,
        showClass = 'is-show',
        expiresDays = 365,
        path = '/'
    } = {}) {
        if (!cookieName) {
            throw new Error('Cookie name is required.');
        }

        if (!(wrapper instanceof HTMLElement) || !(button instanceof HTMLElement)) {
            throw new Error('Wrapper and button elements are required.');
        }

        this.cookieName         = cookieName;
        this.wrapper            = wrapper;
        this.button             = button;
        this.showClass          = showClass;
        this.expiresDays        = expiresDays;
        this.path               = path;
        this.boundAcceptHandler = this.handleAccept.bind(this);
    }

    /**
     * Initializes the panel: shows if not accepted and binds events.
     */
    init() {
        if (this.isAccepted()) {
            return;
        }

        this.show();
        this.bindEvents();
    }

    /**
     * Checks whether the panel was accepted.
     * @returns {boolean} True if accepted.
     */
    isAccepted() {
        return Boolean(getCookie(this.cookieName));
    }

    /**
     * Shows the panel.
     */
    show() {
        this.wrapper.classList.add(this.showClass);
    }

    /**
     * Hides the panel and removes it from DOM.
     */
    hide() {
        this.wrapper.classList.remove(this.showClass);
        if (this.wrapper.parentNode) {
            this.wrapper.parentNode.removeChild(this.wrapper);
        }
        this.unbindEvents();
    }

    bindEvents() {
        this.button.addEventListener('click', this.boundAcceptHandler);
    }

    unbindEvents() {
        this.button.removeEventListener('click', this.boundAcceptHandler);
    }

    handleAccept(event) {
        event.preventDefault();
        setCookie(this.cookieName, 'true', {
            days : this.expiresDays,
            path : this.path
        });
        this.hide();
    }
}

export { CookiesPanel };
